import React, { useEffect, useState } from 'react';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ContractedCarrierForm, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import ContractedCarrierFields, { useContractedCarrierCandidates } from '@components/ringil3/Features/Contracted/ContractedCarrierFields.tsx';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  table: PriceTableForm;
  /** The carrier being edited; undefined adds a new one. */
  initial?: ContractedCarrierForm;
  /** Carriers already on the shipment, not offered again (the edited one is always offered). */
  excludedCarrierIds?: string[];
  infoText?: string;
  highlight?: { levelKey: string; rowKeys: string[] } | null;
  onSetTable?: () => void;
  onSave: (carrier: ContractedCarrierForm) => void;
}

const emptyCarrier = (): ContractedCarrierForm => ({ carrierId: null, carrierName: null, users: [], prices: {} });

/**
 * "Nasmlouvaný dopravce" of one CONTRACTED shipment: carrier, addressed users and its price matrix. Local until
 * "Uložit"; changes apply to this shipment only, never to the Linka it came from.
 */
const ContractedPricesSidebar: React.FC<ComponentProps> = ({ visible, onHide, table, initial, excludedCarrierIds = [], infoText, highlight, onSetTable, onSave }) => {
  const { tr } = useTranslationLgs();
  const candidates = useContractedCarrierCandidates(visible);
  const [value, setValue] = useState<ContractedCarrierForm>(emptyCarrier());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSubmitted(false);
    setValue(initial ? { ...initial, users: [...initial.users], prices: { ...initial.prices } } : emptyCarrier());
  }, [visible]);

  const carrierError = !value.carrierId ? tr('ContractedCarriers.carrierRequired', 'Vyberte dopravce') : undefined;

  const save = () => {
    setSubmitted(true);
    if (carrierError) return;
    onSave(value);
    onHide();
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={initial ? tr('ContractedCarriers.headingEdit', 'Upravit smluvní ceny') : tr('ContractedCarriers.headingNew', 'Přidat dopravce')}
      onBack={onHide}
      onContinue={save}
      continueLabel={tr('ContractedCarriers.save', 'Uložit')}
      size={'md'}
      dataTestIdContinue={'contracted-carrier-save'}
    >
      <div className={'flex flex-col gap-4'}>
        {infoText && <COInlineMessage variant={'info'}>{infoText}</COInlineMessage>}
        <ContractedCarrierFields
          value={value}
          onChange={setValue}
          table={table}
          candidates={candidates}
          excludedCarrierIds={excludedCarrierIds}
          carrierError={submitted ? carrierError : undefined}
          highlight={highlight}
          onSetTable={onSetTable}
          dataTestIdPrefix={'contracted-sidebar'}
        />
      </div>
    </RingilSidebar>
  );
};

export default ContractedPricesSidebar;
